import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Toggle } from '../ui/Toggle';
import { ImageUpload } from '../ui/ImageUpload';
import { Plus, X } from 'lucide-react';
interface PersonalizationField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'dropdown';
  required: boolean;
  characterLimit?: number;
  helperText?: string;
}
interface ProductFormData {
  id?: string;
  name: string;
  category: string;
  description: string;
  image: string;
  basePrice: string;
  sizeGroup: 'youth' | 'adult' | 'both';
  sizes: string[];
  colors: Array<{
    name: string;
    hex: string;
  }>;
  limitInventory: boolean;
  inventoryCaps: Record<string, number>;
  decorationMethod: 'print' | 'embroidery';
  decorationLocations: string[];
  personalizationFields: PersonalizationField[];
}
interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: ProductFormData) => void;
  editProduct?: ProductFormData | null;
}
const YOUTH_SIZES = ['YXS', 'YS', 'YM', 'YL', 'YXL'];
const ADULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
const DECORATION_LOCATIONS = ['Front', 'Back', 'Left Chest', 'Right Sleeve', 'Left Sleeve'];
const PRESET_COLORS = [{
  name: 'Navy',
  hex: '#1e3a8a'
}, {
  name: 'Red',
  hex: '#dc2626'
}, {
  name: 'Black',
  hex: '#000000'
}, {
  name: 'White',
  hex: '#ffffff'
}, {
  name: 'Gray',
  hex: '#6b7280'
}, {
  name: 'Green',
  hex: '#16a34a'
}];
export function AddProductModal({
  isOpen,
  onClose,
  onSave,
  editProduct
}: AddProductModalProps) {
  const [formData, setFormData] = useState<ProductFormData>(editProduct || {
    name: '',
    category: 'T-Shirts',
    description: '',
    image: '',
    basePrice: '',
    sizeGroup: 'both',
    sizes: [],
    colors: [],
    limitInventory: false,
    inventoryCaps: {},
    decorationMethod: 'print',
    decorationLocations: [],
    personalizationFields: []
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customColorName, setCustomColorName] = useState('');
  const [customColorHex, setCustomColorHex] = useState('#000000');
  const availableSizes = formData.sizeGroup === 'youth' ? YOUTH_SIZES : formData.sizeGroup === 'adult' ? ADULT_SIZES : [...YOUTH_SIZES, ...ADULT_SIZES];
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
      newErrors.basePrice = 'Valid price is required';
    }
    if (formData.sizes.length === 0) newErrors.sizes = 'Select at least one size';
    if (formData.colors.length === 0) newErrors.colors = 'Select at least one color';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (!validate()) return;
    onSave(formData);
    onClose();
  };
  const toggleSize = (size: string) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size) ? prev.sizes.filter(s => s !== size) : [...prev.sizes, size]
    }));
  };
  const toggleColor = (color: {
    name: string;
    hex: string;
  }) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.some(c => c.name === color.name) ? prev.colors.filter(c => c.name !== color.name) : [...prev.colors, color]
    }));
  };
  const addCustomColor = () => {
    if (!customColorName.trim()) return;
    toggleColor({
      name: customColorName,
      hex: customColorHex
    });
    setCustomColorName('');
    setCustomColorHex('#000000');
  };
  const toggleLocation = (location: string) => {
    setFormData(prev => ({
      ...prev,
      decorationLocations: prev.decorationLocations.includes(location) ? prev.decorationLocations.filter(l => l !== location) : [...prev.decorationLocations, location]
    }));
  };
  const addPersonalizationField = () => {
    setFormData(prev => ({
      ...prev,
      personalizationFields: [...prev.personalizationFields, {
        id: `field_${Date.now()}`,
        label: '',
        type: 'text',
        required: false,
        helperText: ''
      }]
    }));
  };
  const updatePersonalizationField = (id: string, updates: Partial<PersonalizationField>) => {
    setFormData(prev => ({
      ...prev,
      personalizationFields: prev.personalizationFields.map(field => field.id === id ? {
        ...field,
        ...updates
      } : field)
    }));
  };
  const removePersonalizationField = (id: string) => {
    setFormData(prev => ({
      ...prev,
      personalizationFields: prev.personalizationFields.filter(f => f.id !== id)
    }));
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={editProduct ? 'Edit Product' : 'Add Product'} size="xl" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-327">
            Cancel
          </Button>
          <Button onClick={handleSave} data-id="element-328">
            {editProduct ? 'Update Product' : 'Save Product'}
          </Button>
        </>} data-id="element-326">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2" data-id="element-329">
        {/* Product Basics */}
        <div data-id="element-330">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-331">
            Product Basics
          </h4>
          <div className="space-y-4" data-id="element-332">
            <Input label="Product Name *" value={formData.name} onChange={e => setFormData(prev => ({
            ...prev,
            name: e.target.value
          }))} error={errors.name} placeholder="e.g., Performance T-Shirt" data-id="element-333" />

            <Select label="Category" value={formData.category} onChange={e => setFormData(prev => ({
            ...prev,
            category: e.target.value
          }))} options={[{
            value: 'T-Shirts',
            label: 'T-Shirts'
          }, {
            value: 'Hoodies',
            label: 'Hoodies'
          }, {
            value: 'Hats',
            label: 'Hats'
          }, {
            value: 'Bags',
            label: 'Bags'
          }, {
            value: 'Bundles',
            label: 'Bundles'
          }, {
            value: 'Other',
            label: 'Other'
          }]} data-id="element-334" />

            <div data-id="element-335">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-336">
                Description
              </label>
              <textarea value={formData.description} onChange={e => setFormData(prev => ({
              ...prev,
              description: e.target.value
            }))} className="w-full rounded-md border border-gray-300 p-2 text-sm focus:ring-red-500 focus:border-red-500" rows={3} placeholder="Product description..." data-id="element-337" />
            </div>

            <ImageUpload label="Product Image" value={formData.image} onChange={imageUrl => setFormData(prev => ({
            ...prev,
            image: imageUrl
          }))} data-id="element-338" />

            <Input label="Base Price *" type="number" step="0.01" value={formData.basePrice} onChange={e => setFormData(prev => ({
            ...prev,
            basePrice: e.target.value
          }))} error={errors.basePrice} placeholder="29.99" data-id="element-339" />
          </div>
        </div>

        {/* Variants */}
        <div className="border-t border-gray-200 pt-6" data-id="element-340">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-341">Variants</h4>

          <div className="space-y-4" data-id="element-342">
            <div data-id="element-343">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-344">
                Size Group
              </label>
              <div className="flex space-x-2" data-id="element-345">
                {['youth', 'adult', 'both'].map(group => <button key={group} onClick={() => setFormData(prev => ({
                ...prev,
                sizeGroup: group as any,
                sizes: []
              }))} className={`
                      px-4 py-2 rounded-md text-sm font-medium transition-colors
                      ${formData.sizeGroup === group ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                    `} data-id="element-346">
                    {group.charAt(0).toUpperCase() + group.slice(1)}
                  </button>)}
              </div>
            </div>

            <div data-id="element-347">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-348">
                Sizes *{' '}
                {errors.sizes && <span className="text-red-600 text-xs ml-2" data-id="element-349">
                    {errors.sizes}
                  </span>}
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-350">
                {availableSizes.map(size => <button key={size} onClick={() => toggleSize(size)} className={`
                      px-3 py-2 rounded-md text-sm font-medium border-2 transition-colors
                      ${formData.sizes.includes(size) ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-200 text-gray-700 hover:border-gray-300'}
                    `} data-id="element-351">
                    {size}
                  </button>)}
              </div>
            </div>

            <div data-id="element-352">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-353">
                Colors *{' '}
                {errors.colors && <span className="text-red-600 text-xs ml-2" data-id="element-354">
                    {errors.colors}
                  </span>}
              </label>
              <div className="flex flex-wrap gap-2 mb-3" data-id="element-355">
                {PRESET_COLORS.map(color => <button key={color.name} onClick={() => toggleColor(color)} className={`
                      flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium border-2 transition-colors
                      ${formData.colors.some(c => c.name === color.name) ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-gray-300'}
                    `} data-id="element-356">
                    <div className="w-4 h-4 rounded-full border border-gray-300" style={{
                  backgroundColor: color.hex
                }} data-id="element-357" />
                    <span data-id="element-358">{color.name}</span>
                  </button>)}
              </div>

              <div className="flex items-end space-x-2" data-id="element-359">
                <Input label="Custom Color" value={customColorName} onChange={e => setCustomColorName(e.target.value)} placeholder="Color name" className="flex-1" data-id="element-360" />
                <div className="flex-shrink-0" data-id="element-361">
                  <input type="color" value={customColorHex} onChange={e => setCustomColorHex(e.target.value)} className="h-10 w-16 rounded border border-gray-300 cursor-pointer" data-id="element-362" />
                </div>
                <Button variant="outline" onClick={addCustomColor} disabled={!customColorName.trim()} data-id="element-363">
                  Add
                </Button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-364">
              <Toggle label="Limit Inventory Quantities" checked={formData.limitInventory} onChange={checked => setFormData(prev => ({
              ...prev,
              limitInventory: checked
            }))} description="Set maximum quantities per variant" data-id="element-365" />

              {formData.limitInventory && formData.sizes.length > 0 && formData.colors.length > 0 && <div className="mt-4 overflow-x-auto" data-id="element-366">
                    <table className="min-w-full text-sm" data-id="element-367">
                      <thead data-id="element-368">
                        <tr className="border-b border-gray-200" data-id="element-369">
                          <th className="text-left py-2 px-2 font-medium text-gray-700" data-id="element-370">
                            Size / Color
                          </th>
                          {formData.colors.map(color => <th key={color.name} className="text-center py-2 px-2 font-medium text-gray-700" data-id="element-371">
                              {color.name}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody data-id="element-372">
                        {formData.sizes.map(size => <tr key={size} className="border-b border-gray-100" data-id="element-373">
                            <td className="py-2 px-2 font-medium text-gray-900" data-id="element-374">
                              {size}
                            </td>
                            {formData.colors.map(color => {
                      const key = `${size}-${color.name}`;
                      return <td key={color.name} className="py-2 px-2" data-id="element-375">
                                  <input type="number" min="0" value={formData.inventoryCaps[key] || ''} onChange={e => setFormData(prev => ({
                          ...prev,
                          inventoryCaps: {
                            ...prev.inventoryCaps,
                            [key]: parseInt(e.target.value) || 0
                          }
                        }))} className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-red-500 focus:border-red-500" placeholder="∞" data-id="element-376" />
                                </td>;
                    })}
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
            </div>
          </div>
        </div>

        {/* Decoration & Personalization */}
        <div className="border-t border-gray-200 pt-6" data-id="element-377">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-378">
            Decoration & Personalization
          </h4>

          <div className="space-y-4" data-id="element-379">
            <div data-id="element-380">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-381">
                Decoration Method
              </label>
              <div className="flex space-x-2" data-id="element-382">
                {['print', 'embroidery'].map(method => <button key={method} onClick={() => setFormData(prev => ({
                ...prev,
                decorationMethod: method as any
              }))} className={`
                      px-4 py-2 rounded-md text-sm font-medium transition-colors
                      ${formData.decorationMethod === method ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                    `} data-id="element-383">
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </button>)}
              </div>
            </div>

            <div data-id="element-384">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-385">
                Decoration Locations
              </label>
              <div className="space-y-2" data-id="element-386">
                {DECORATION_LOCATIONS.map(location => <label key={location} className="flex items-center" data-id="element-387">
                    <input type="checkbox" checked={formData.decorationLocations.includes(location)} onChange={() => toggleLocation(location)} className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded" data-id="element-388" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-389">
                      {location}
                    </span>
                  </label>)}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-390">
              <div className="flex items-center justify-between mb-3" data-id="element-391">
                <label className="block text-sm font-medium text-gray-700" data-id="element-392">
                  Personalization Fields (Optional)
                </label>
                <Button variant="outline" size="sm" onClick={addPersonalizationField} data-id="element-393">
                  <Plus className="h-4 w-4 mr-1" data-id="element-394" />
                  Add Field
                </Button>
              </div>

              {formData.personalizationFields.length > 0 && <div className="space-y-3" data-id="element-395">
                  {formData.personalizationFields.map(field => <div key={field.id} className="border border-gray-200 rounded-lg p-3 space-y-3" data-id="element-396">
                      <div className="flex items-start justify-between" data-id="element-397">
                        <div className="flex-1 grid grid-cols-2 gap-3" data-id="element-398">
                          <Input label="Field Label" value={field.label} onChange={e => updatePersonalizationField(field.id, {
                      label: e.target.value
                    })} placeholder="e.g., Player Name" data-id="element-399" />
                          <Select label="Field Type" value={field.type} onChange={e => updatePersonalizationField(field.id, {
                      type: e.target.value as any
                    })} options={[{
                      value: 'text',
                      label: 'Text'
                    }, {
                      value: 'number',
                      label: 'Number'
                    }, {
                      value: 'dropdown',
                      label: 'Dropdown'
                    }]} data-id="element-400" />
                        </div>
                        <button onClick={() => removePersonalizationField(field.id)} className="ml-2 p-1 text-red-600 hover:text-red-700" data-id="element-401">
                          <X className="h-5 w-5" data-id="element-402" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3" data-id="element-403">
                        <div className="flex items-center" data-id="element-404">
                          <input type="checkbox" checked={field.required} onChange={e => updatePersonalizationField(field.id, {
                      required: e.target.checked
                    })} className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded" data-id="element-405" />
                          <label className="ml-2 text-sm text-gray-700" data-id="element-406">
                            Required
                          </label>
                        </div>

                        {field.type === 'text' && <Input label="Character Limit" type="number" value={field.characterLimit || ''} onChange={e => updatePersonalizationField(field.id, {
                    characterLimit: parseInt(e.target.value) || undefined
                  })} placeholder="e.g., 20" data-id="element-407" />}
                      </div>

                      <Input label="Helper Text" value={field.helperText || ''} onChange={e => updatePersonalizationField(field.id, {
                  helperText: e.target.value
                })} placeholder="Additional instructions for customers" data-id="element-408" />
                    </div>)}
                </div>}
            </div>
          </div>
        </div>
      </div>
    </Modal>;
}